import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import SpinnerButton from '../Common/SpinnerButton';
import BoldButton from '../Common/BoldButton';
import KaAlert from '../Common/KaAlert';
import useDisplayMessage from '../Common/useDisplayMessage';
import { KaSelect, KaSelectOption } from '../Common/KaSelect';
import { EmailSettingsDto } from '../Requests/EmailSettings/useGetEmailSettings';
import { EmailConfigurationUpdate } from '../Requests/EmailSettings/useUpdateEmailSettings';
import useGetUsers from '../Requests/useGetUsers';

type Tab = 'ticketConfirmation' | 'errorNotifications';

interface EmailConfigurationFormProps {
  settings: EmailSettingsDto;
  onSave: (formData: EmailConfigurationUpdate) => void;
  isLoading: boolean;
}

const EmailConfigurationForm: React.FC<EmailConfigurationFormProps> = ({
  settings,
  onSave,
  isLoading
}) => {
  const { register, handleSubmit, watch } = useForm<EmailConfigurationUpdate>({
    defaultValues: {
      orderTransactionsIsEnabled: settings.orderTransactionsIsEnabled,
      orderTransactionsSendToCustomers: settings.orderTransactionsSendToCustomers,
      orderTransactionsSendToBranches: settings.orderTransactionsSendToBranches,
      orderTransactionsSendToDrivers: settings.orderTransactionsSendToDrivers,
      orderTransactionsSendToCarriers: settings.orderTransactionsSendToCarriers,
      orderTransactionsSendToApplicators: settings.orderTransactionsSendToApplicators,
    }
  });

  const [activeTab, setActiveTab] = useState<Tab>('ticketConfirmation');
  const [connectorErrorsIsEnabled, setConnectorErrorsIsEnabled] = useState(settings.connectorErrorsIsEnabled);
  const [recipients, setRecipients] = useState<string[]>(settings.connectorErrorsRecipients);
  const [showAddInput, setShowAddInput] = useState(false);
  const displayMessage = useDisplayMessage();

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const addRecipient = (option: KaSelectOption | null) => {
    if (!option) return;
    setRecipients(prev => [...prev, option.value]);
    setShowAddInput(false);
    displayMessage.clear();
  };

  const removeRecipient = (index: number) =>
    setRecipients(recipients.filter((_, i) => i !== index));

  const isEnabledValue = watch('orderTransactionsIsEnabled');

  const getUsers = useGetUsers();
  const userOptions: KaSelectOption[] = getUsers.data?.map(u => ({ value: u.emailAddress, label: u.emailAddress })) ?? [];

  const handleSave = (formValues: EmailConfigurationUpdate) => {
    const invalid = recipients.filter(r => !isValidEmail(r));
    if (invalid.length > 0) {
      displayMessage.fail(`Invalid email address: ${invalid.join(', ')}`);
      return;
    }
    onSave({ ...formValues, connectorErrorsIsEnabled, connectorErrorsRecipients: recipients });
  };

  return (
    <>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <ul className="nav nav-tabs mb-3" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            type="button"
            role="tab"
            className={`nav-link ${activeTab === 'ticketConfirmation' ? 'active' : ''}`}
            onClick={() => setActiveTab('ticketConfirmation')}>
            Ticket Confirmation
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            role="tab"
            className={`nav-link ${activeTab === 'errorNotifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('errorNotifications')}>
            Error Notifications
          </button>
        </li>
      </ul>

      {activeTab === 'ticketConfirmation' && (
        <div className="row m-2 p-3 mb-4">
          <div className="col-12 mb-2">
            <div className="row">
              <div className="col-11">
                <label className="text-dark fw-semibold" htmlFor="emailEnabled">
                  Enable Ticket Confirmation Emails
                </label>
                <p><small>Automatically send confirmation emails when tickets are created or updated</small></p>
              </div>
              <div className="col-1 text-end form-check form-switch">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="emailEnabled"
                  {...register('orderTransactionsIsEnabled')}
                />
              </div>
            </div>
          </div>
          <div className="col-12">
            <h5 className="mb-2">Select Email Recipients</h5>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="sendToCustomers"
                disabled={!isEnabledValue}
                {...register('orderTransactionsSendToCustomers')}
              />
              <label className="form-check-label" htmlFor="sendToCustomers">
                Customers
              </label>
            </div>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="sendToBranches"
                disabled={!isEnabledValue}
                {...register('orderTransactionsSendToBranches')}
              />
              <label className="form-check-label" htmlFor="sendToBranches">
                Branches
              </label>
            </div>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="sendToDrivers"
                disabled={!isEnabledValue}
                {...register('orderTransactionsSendToDrivers')}
              />
              <label className="form-check-label" htmlFor="sendToDrivers">
                Drivers
              </label>
            </div>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="sendToCarriers"
                disabled={!isEnabledValue}
                {...register('orderTransactionsSendToCarriers')}
              />
              <label className="form-check-label" htmlFor="sendToCarriers">
                Carriers
              </label>
            </div>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="sendToApplicators"
                disabled={!isEnabledValue}
                {...register('orderTransactionsSendToApplicators')}
              />
              <label className="form-check-label" htmlFor="sendToApplicators">
                Applicators
              </label>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'errorNotifications' && (
        <div className="row m-2 p-3 mb-4">
          <div className="col-12 mb-2">
            <div className="row">
              <div className="col-11">
                <label className="text-dark fw-semibold" htmlFor="connectorErrorsIsEnabled">
                  Enable Error Notification Emails
                </label>
                <p><small>Send automated alerts when system errors or critical issues occur to specific users</small></p>
              </div>
              <div className="col-1 text-end form-check form-switch">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="connectorErrorsIsEnabled"
                  checked={connectorErrorsIsEnabled}
                  onChange={(e) => setConnectorErrorsIsEnabled(e.target.checked)}
                />
              </div>
            </div>
          </div>
          <div className="col-12">
            <h5 className="mb-2">Authorized Users for Error Notifications</h5>
            <p><small>Only the following users will receive error notification emails</small></p>
            {recipients.length > 0 && (
              <div className="mb-3">
                {recipients.map((recipient, index) => (
                  <div key={index} className="d-flex align-items-center justify-content-between p-3 bg-white border rounded mb-2">
                    <span className="text-dark">{recipient}</span>
                    <BoldButton className="btn-ghost-danger" disabled={!connectorErrorsIsEnabled} onClick={() => removeRecipient(index)} aria-label={`Remove ${recipient}`}>
                      <i className="bi bi-x" />
                    </BoldButton>
                  </div>
                ))}
              </div>
            )}
            {showAddInput ? (
              <div className="d-flex gap-2 align-items-center">
                <div className="flex-grow-1">
                  <KaSelect
                    allowCreate
                    autoFocus
                    isDisabled={!connectorErrorsIsEnabled}
                    options={userOptions.filter(o => !recipients.includes(o.value))}
                    value={null}
                    onChange={selected => addRecipient(selected as KaSelectOption)}
                    formatCreateLabel={(input) => `Add "${input}"`}
                    placeholder="Select or enter an email address..."
                  />
                </div>
                <BoldButton className="btn-ghost-secondary" onClick={() => setShowAddInput(false)}>Cancel</BoldButton>
              </div>
            ) : (
              <BoldButton className="btn-ghost-primary w-100" style={{ borderStyle: 'dashed' }} disabled={!connectorErrorsIsEnabled} onClick={() => setShowAddInput(true)}>
                + Add Email
              </BoldButton>
            )}
          </div>
        </div>
      )}

      <div className="text-end">
        <SpinnerButton
          className="btn-ghost-primary"
          spinning={isLoading}
          onClick={handleSubmit(handleSave)}>
          Save
        </SpinnerButton>
      </div>
    </>
  );
};

export default EmailConfigurationForm;
